import { useEffect, RefObject } from 'react'

/**
 * Hook that detects clicks outside of a specified element
 * Useful for closing dropdowns, modals, etc. when clicking outside
 *
 * @param ref - Ref to the element to detect clicks outside of
 * @param handler - Callback function to call when click outside is detected
 * @param enabled - Whether the hook is enabled (default: true)
 */
export const useClickOutside = (
  ref: RefObject<HTMLElement>,
  handler: () => void,
  enabled: boolean = true
): void => {
  useEffect(() => {
    if (!enabled) {
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      // If the ref element exists and the click is outside of it
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler()
      }
    }

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside)

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [ref, handler, enabled])
}
